import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    ShieldCheck, AlertTriangle, MapPin, ArrowRight, LogIn, Info, Loader2,
} from "lucide-react";
import axios from "axios";
import Logo from "@/components/Logo";
import useSeo from "@/lib/useSeo";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const MODEL_LABEL = {
    alfa: "Modelo Alfa",
    omega: "Modelo Ômega",
    custom: "Modelo personalizado",
};

/**
 * Página pública do "Passaporte digital do carrinho" (aberta via QR).
 * Mostra apenas info mínima. Para tudo mais → convite a entrar/registar.
 */
export default function CartPublic() {
    const { serial } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    useSeo({
        title: `Carrinho ${serial} · NOVIFOOD`,
        description: "Cartão digital do carrinho NOVIFOOD — verifique estado, garantia e reporte extravios.",
        path: `/carrinho/${serial}`,
    });

    useEffect(() => {
        let alive = true;
        setLoading(true);
        axios.get(`${API}/carts/${encodeURIComponent(serial)}/public`)
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => {
                if (!alive) return;
                if (e?.response?.status === 404) setNotFound(true);
            })
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [serial]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary/50" />
            </div>
        );
    }

    if (notFound) {
        return (
            <section className="min-h-[70vh] flex items-center">
                <div className="container-x max-w-xl text-center">
                    <AlertTriangle className="h-10 w-10 text-secondary mx-auto" />
                    <h1 className="mt-5 font-heading text-3xl font-extrabold text-primary">
                        Carrinho não encontrado
                    </h1>
                    <p className="mt-4 text-muted-foreground">
                        Não localizámos nenhum carrinho NOVIFOOD com o número de série <code className="font-mono text-primary">{serial}</code>.
                        Verifique se leu o QR correctamente.
                    </p>
                    <Link to="/" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-primary hover:underline">
                        Voltar ao site <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </section>
        );
    }

    const isLost = !!data?.reported_lost;
    const modelLabel = MODEL_LABEL[data?.model] || data?.model;

    return (
        <>
            {/* HERO minimal */}
            <section className={`relative border-b ${isLost ? "gradient-navy" : "bg-white"}`}>
                {isLost && <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />}
                <div className="container-x relative py-14 sm:py-20 max-w-3xl">
                    <div className="flex items-center gap-3">
                        <Logo testId="cart-passport-logo" size="sm" />
                        <span className={`text-[10px] font-bold uppercase tracking-[0.24em] ${isLost ? "text-secondary" : "text-primary/60"}`}>
                            Cartão do carrinho
                        </span>
                    </div>
                    <h1 className={`mt-8 font-heading text-4xl sm:text-5xl font-extrabold leading-tight ${isLost ? "text-white" : "text-primary"}`}>
                        {data?.nickname || modelLabel}
                    </h1>
                    <p className={`mt-2 font-mono text-sm tracking-wider ${isLost ? "text-secondary" : "text-primary/70"}`}>
                        Nº série · <span className="font-bold">{data?.serial_number}</span>
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-wider"
                         style={isLost
                            ? { borderColor: "#fca5a5", backgroundColor: "#7f1d1d", color: "#fff" }
                            : { borderColor: "hsl(var(--border))", backgroundColor: "#f5f5f4", color: "#0F172A" }}>
                        <span className={`inline-block h-2 w-2 rounded-full ${isLost ? "bg-red-400" : "bg-emerald-500"}`} />
                        {isLost ? "Reportado como perdido/roubado" : "Activo"}
                    </div>
                </div>
            </section>

            {/* MAIN CARD */}
            <section className="section-y">
                <div className="container-x max-w-3xl space-y-6">
                    {isLost && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 p-6" data-testid="cart-lost-warning">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="h-6 w-6 text-red-600 shrink-0 mt-0.5" />
                                <div>
                                    <h2 className="font-heading text-lg font-extrabold text-red-900">
                                        Este carrinho foi reportado como perdido/roubado
                                    </h2>
                                    <p className="mt-2 text-sm text-red-900/80 leading-relaxed">
                                        Se está de posse dele ou tem informação sobre a sua localização, por favor contacte-nos.
                                        A NOVIFOOD irá coordenar a devolução ao legítimo proprietário.
                                    </p>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        <a
                                            href="mailto:apoio@novifood.pt"
                                            className="inline-flex items-center gap-2 rounded-full bg-red-700 px-4 py-2 text-sm font-bold text-white hover:bg-red-800 transition"
                                        >
                                            Contactar NOVIFOOD
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="rounded-2xl border border-border bg-white p-6 lg:p-8">
                        <div className="flex items-start gap-4">
                            <ShieldCheck className="h-8 w-8 text-secondary shrink-0" />
                            <div className="flex-1">
                                <h2 className="font-heading text-lg font-extrabold text-primary">
                                    Passaporte digital
                                </h2>
                                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                                    Cada carrinho NOVIFOOD tem um cartão digital único — como uma matrícula.
                                    Aqui encontra o histórico de manutenção, garantia e peças originais.
                                </p>
                            </div>
                        </div>

                        <dl className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-6 border-t border-border pt-6">
                            <div>
                                <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary/50">Modelo</dt>
                                <dd className="mt-1 font-bold text-primary">{modelLabel}</dd>
                            </div>
                            <div>
                                <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary/50">Estado</dt>
                                <dd className="mt-1 font-bold text-primary capitalize">{isLost ? "—" : (data?.status || "activo")}</dd>
                            </div>
                            {data?.nickname && (
                                <div>
                                    <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary/50">Nome</dt>
                                    <dd className="mt-1 font-bold text-primary truncate">{data.nickname}</dd>
                                </div>
                            )}
                        </dl>
                    </div>

                    {/* CTA cards */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <Link
                            to="/entrar"
                            state={{ next: `/conta/carrinhos/${serial}` }}
                            data-testid="cart-cta-signin"
                            className="rounded-2xl border-2 border-primary bg-primary text-white p-6 hover:-translate-y-0.5 transition"
                        >
                            <LogIn className="h-6 w-6 text-secondary" />
                            <div className="mt-4 font-heading text-lg font-extrabold">
                                Sou o proprietário
                            </div>
                            <p className="mt-1 text-sm text-white/80 leading-snug">
                                Entre para ver histórico completo, próxima manutenção, peças e pedir assistência.
                            </p>
                            <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-secondary">
                                Entrar <ArrowRight className="h-4 w-4" />
                            </div>
                        </Link>

                        {!isLost && (
                            <button
                                type="button"
                                onClick={() => setReportOpen(true)}
                                data-testid="cart-cta-report"
                                className="rounded-2xl border-2 border-border bg-white p-6 text-left hover:border-red-300 hover:-translate-y-0.5 transition"
                            >
                                <AlertTriangle className="h-6 w-6 text-red-500" />
                                <div className="mt-4 font-heading text-lg font-extrabold text-primary">
                                    Encontrei este carrinho
                                </div>
                                <p className="mt-1 text-sm text-muted-foreground leading-snug">
                                    Suspeita que foi extraviado? Reporte anonimamente e a NOVIFOOD contacta o dono.
                                </p>
                                <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-red-600">
                                    Reportar <ArrowRight className="h-4 w-4" />
                                </div>
                            </button>
                        )}
                    </div>

                    <div className="rounded-xl bg-muted/60 border border-border p-4 flex items-start gap-3">
                        <Info className="h-4 w-4 text-primary/60 shrink-0 mt-0.5" />
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            Este cartão é público para permitir a devolução de carrinhos extraviados.
                            Informação detalhada (histórico, peças, cliente) está protegida e só o proprietário pode ver depois de fazer login.
                        </p>
                    </div>

                    <div className="text-center pt-4">
                        <Link to="/" className="text-xs text-primary/60 hover:text-primary font-bold uppercase tracking-[0.18em] hover:underline">
                            Voltar ao site NOVIFOOD
                        </Link>
                    </div>
                </div>
            </section>

            {reportOpen && (
                <ReportLostModal
                    serial={serial}
                    onClose={() => setReportOpen(false)}
                    onDone={() => {
                        setReportOpen(false);
                        // refresh
                        axios.get(`${API}/carts/${encodeURIComponent(serial)}/public`)
                            .then((r) => setData(r.data)).catch(() => {});
                    }}
                />
            )}
        </>
    );
}

function ReportLostModal({ serial, onClose, onDone }) {
    const [contact, setContact] = useState("");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!contact.trim()) {
            toast.error("Indique um contacto (email ou telefone).");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/carts/${encodeURIComponent(serial)}/report-lost`, {
                contact: contact.trim(),
                note: note.trim() || null,
            });
            toast.success("Reporte enviado. A NOVIFOOD vai analisar e contactar o proprietário.");
            onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Não foi possível enviar o reporte.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            role="dialog"
            data-testid="report-lost-modal"
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg rounded-2xl bg-white p-6 lg:p-8 shadow-2xl"
            >
                <div className="flex items-center gap-3">
                    <MapPin className="h-6 w-6 text-red-500" />
                    <h2 className="font-heading text-xl font-extrabold text-primary">Reportar carrinho extraviado</h2>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                    Serial: <span className="font-mono font-bold text-primary">{serial}</span>
                </p>
                <div className="mt-5 space-y-4">
                    <label className="block">
                        <span className="text-xs font-bold uppercase tracking-wide text-primary/70">
                            O seu contacto *
                        </span>
                        <input
                            type="text"
                            value={contact}
                            onChange={(e) => setContact(e.target.value)}
                            placeholder="Email ou telemóvel"
                            required
                            data-testid="report-contact"
                            className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs font-bold uppercase tracking-wide text-primary/70">
                            Detalhes (opcional)
                        </span>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={4}
                            placeholder="Onde e quando o viu, condição, etc."
                            data-testid="report-note"
                            className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary"
                        />
                    </label>
                </div>
                <div className="mt-6 flex flex-wrap justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center rounded-full border border-border bg-white px-5 py-2 text-sm font-bold text-primary hover:bg-muted transition"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="report-submit"
                        className="inline-flex items-center rounded-full bg-red-600 px-5 py-2 text-sm font-bold text-white hover:bg-red-700 transition disabled:opacity-60"
                    >
                        {submitting ? "A enviar..." : "Enviar reporte"}
                    </button>
                </div>
            </form>
        </div>
    );
}
